import React, { FC, useState, useEffect } from 'react';
import { Modal } from 'antd';
import {
  DayKey,
  DAYS_CONFIG,
  DEFAULT_WEEKLY_SCHEDULE,
  WeeklySchedule,
  TimeSlot,
  getBrowserTimezone,
  timeToMinutes,
  minutesToTime,
  doSlotsOverlap,
  validateDaySlots,
  isSlotConflicting,
  sortSlots,
} from 'src/screens/OnboardingTrainer/screens/Schedule/utils/timezoneUtils';
import { TimeSlotPicker } from 'src/screens/OnboardingTrainer/screens/Schedule/components/TimeSlotPicker';
import { TimezoneSelector } from 'src/screens/OnboardingTrainer/screens/Schedule/components/TimezoneSelector';
import {
  DEFAULT_SLOT_PROGRESSION,
  MAX_SLOTS_PER_DAY,
  SCHEDULE_SYNC_DAYS_AHEAD,
} from 'src/screens/OnboardingTrainer/screens/Schedule/Schedule';
import {
  useGetTrainerSchedule,
  UnavailableDateEntry,
  ConnectedCalendarInfo,
} from 'src/api/queries/useGetTrainerSchedule';
import { useUpdateTrainerSchedule } from 'src/api/mutations/useUpdateTrainerSchedule';
import { ScheduleConflict } from 'src/api/mutations/useCheckScheduleConflicts';
import { sessionStore } from 'src/store/sessionStore';
import { getUserIdFromToken } from 'src/utils/authUtils';
import toast from 'src/utils/appAlert';
import { ConnectCalendarModal } from './ConnectCalendarModal/ConnectCalendarModal';
import { UnavailableDatesModal } from './UnavailableDatesModal/UnavailableDatesModal';
import './ScheduleSettings.scss';

const settingsModalStyles = {
  mask: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(6, 7, 35, 0.8)',
  },
  container: {
    background: 'linear-gradient(180deg, #101438 0%, #070924 100%)',
    backgroundColor: '#0c0f2b',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '20px',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6)',
    padding: '32px 28px',
    color: '#ffffff',
    overflow: 'hidden',
  },
  body: {
    padding: 0,
    background: 'transparent',
  },
};

export const ScheduleSettings: FC = () => {
  const token = sessionStore((s) => s.token);
  const userId = getUserIdFromToken(token);

  // Timezone and Weekly Schedule state
  const [timezone, setTimezone] = useState<string>(() => getBrowserTimezone() || 'UTC');
  const [schedule, setSchedule] = useState<WeeklySchedule>(DEFAULT_WEEKLY_SCHEDULE);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [dayErrors, setDayErrors] = useState<Partial<Record<DayKey, string>>>({});

  // AI Responses toggle state
  const [aiResponsesEnabled, setAiResponsesEnabled] = useState<boolean>(true);

  // Confirmation modal state for turning off an active day
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    dayKey: DayKey | null;
  }>({
    isOpen: false,
    dayKey: null,
  });

  // Conflict modal state when updating schedule
  const [conflictModal, setConflictModal] = useState<{
    isOpen: boolean;
    conflicts: ScheduleConflict[];
  }>({
    isOpen: false,
    conflicts: [],
  });

  // Calendar connect modal state
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [connectedCalendar, setConnectedCalendar] = useState<ConnectedCalendarInfo | null>(null);

  // Unavailable dates modal state
  const [unavailableModalOpen, setUnavailableModalOpen] = useState(false);
  const [focusedDateForModal, setFocusedDateForModal] = useState<string | null>(null);
  const [unavailableDatesList, setUnavailableDatesList] = useState<UnavailableDateEntry[]>([]);

  // Queries and mutations
  const { data: existingScheduleData, isLoading: isScheduleLoading } = useGetTrainerSchedule(
    userId || undefined,
  );
  const {
    mutate: updateSchedule,
    mutateAsync: updateScheduleAsync,
    isPending: isUpdatingSchedule,
  } = useUpdateTrainerSchedule();

  // Hydrate from existing backend data
  useEffect(() => {
    if (existingScheduleData?.data) {
      if (existingScheduleData.data.timezone) {
        setTimezone(existingScheduleData.data.timezone);
      }
      if (
        existingScheduleData.data.weekly_schedule &&
        Object.keys(existingScheduleData.data.weekly_schedule).length > 0
      ) {
        setSchedule({
          ...DEFAULT_WEEKLY_SCHEDULE,
          ...existingScheduleData.data.weekly_schedule,
        });
      }
      if (typeof existingScheduleData.data.ai_responses_enabled === 'boolean') {
        setAiResponsesEnabled(existingScheduleData.data.ai_responses_enabled);
      }
      if (Array.isArray(existingScheduleData.data.unavailable_dates)) {
        setUnavailableDatesList(existingScheduleData.data.unavailable_dates);
      }
      if (existingScheduleData.data.connected_calendar) {
        setConnectedCalendar(existingScheduleData.data.connected_calendar);
      }
    }
  }, [existingScheduleData]);

  // Handle Day Toggle
  const handleToggleDay = (dayKey: DayKey) => {
    setDayErrors((prev) => {
      const next = { ...prev };
      delete next[dayKey];
      return next;
    });

    const currentDay = schedule?.[dayKey] || DEFAULT_WEEKLY_SCHEDULE[dayKey];
    if (currentDay.enabled) {
      if (currentDay.slots && currentDay.slots.length > 0) {
        setConfirmModal({ isOpen: true, dayKey });
        return;
      }
      setSchedule((prev) => ({
        ...prev,
        [dayKey]: {
          ...(prev?.[dayKey] || DEFAULT_WEEKLY_SCHEDULE[dayKey]),
          enabled: false,
        },
      }));
    } else {
      const defaultSlots =
        currentDay.slots && currentDay.slots.length > 0
          ? currentDay.slots
          : [{ id: `slot-${dayKey}-1`, start: '09:00', end: '17:00' }];

      setSchedule((prev) => ({
        ...prev,
        [dayKey]: {
          enabled: true,
          slots: defaultSlots,
        },
      }));
    }
  };

  // Confirm disabling day
  const handleConfirmDisable = () => {
    if (confirmModal.dayKey) {
      const dayKey = confirmModal.dayKey;
      setDayErrors((prev) => {
        const next = { ...prev };
        delete next[dayKey];
        return next;
      });
      setSchedule((prev) => ({
        ...prev,
        [dayKey]: {
          enabled: false,
          slots: [],
        },
      }));
    }
    setConfirmModal({ isOpen: false, dayKey: null });
  };

  // Add slot to day
  const handleAddSlot = (dayKey: DayKey) => {
    const day = schedule[dayKey];
    if (day.slots.length >= MAX_SLOTS_PER_DAY) {
      setDayErrors((prev) => ({
        ...prev,
        [dayKey]: `Maximum ${MAX_SLOTS_PER_DAY} slots allowed per day.`,
      }));
      return;
    }

    const sorted = sortSlots(day.slots);
    const currentCount = sorted.length;
    const nextCount = currentCount + 1;
    const currentPattern = DEFAULT_SLOT_PROGRESSION[currentCount];
    const nextPattern = DEFAULT_SLOT_PROGRESSION[nextCount];

    const isMatchingDefaultPattern =
      Boolean(currentPattern && nextPattern) &&
      sorted.every(
        (slot, idx) =>
          slot.start === currentPattern[idx]?.start && slot.end === currentPattern[idx]?.end,
      );

    let updatedSlots: TimeSlot[] = [];

    if (isMatchingDefaultPattern && nextPattern) {
      updatedSlots = nextPattern.map((slotDef, idx) => ({
        id: sorted[idx]?.id || `slot-${dayKey}-${Date.now()}-${idx + 1}`,
        start: slotDef.start,
        end: slotDef.end,
      }));
    } else {
      const lastSlot = sorted[sorted.length - 1];
      let newStart = '13:00';
      let newEnd = '17:00';

      if (lastSlot) {
        const lastEndMin = timeToMinutes(lastSlot.end);
        if (lastEndMin + 60 <= 1440) {
          newStart = minutesToTime(lastEndMin);
          newEnd = minutesToTime(Math.min(1440, lastEndMin + 240));
        } else {
          newStart = '08:00';
          newEnd = '12:00';
        }
      }

      const hasOverlap = sorted.some((s) => doSlotsOverlap(s.start, s.end, newStart, newEnd));

      if (hasOverlap) {
        let foundStart: number | null = null;
        for (let m = 0; m <= 1380; m += 30) {
          const testStart = minutesToTime(m);
          const testEnd = minutesToTime(m + 60);
          if (!sorted.some((s) => doSlotsOverlap(s.start, s.end, testStart, testEnd))) {
            foundStart = m;
            break;
          }
        }

        if (foundStart !== null) {
          newStart = minutesToTime(foundStart);
          newEnd = minutesToTime(foundStart + 60);
        } else {
          setDayErrors((prev) => ({
            ...prev,
            [dayKey]: 'No available non-overlapping time slot left on this day.',
          }));
          return;
        }
      }

      const newSlot: TimeSlot = {
        id: `slot-${dayKey}-${Date.now()}`,
        start: newStart,
        end: newEnd,
      };

      updatedSlots = sortSlots([...day.slots, newSlot]);
    }

    const err = validateDaySlots(updatedSlots);
    setDayErrors((prev) => {
      const next = { ...prev };
      if (err) {
        next[dayKey] = err;
      } else {
        delete next[dayKey];
      }
      return next;
    });

    setSchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: updatedSlots,
      },
    }));
  };

  // Remove slot from day
  const handleRemoveSlot = (dayKey: DayKey, slotId: string) => {
    const day = schedule[dayKey];
    const updatedSlots = day.slots.filter((s) => s.id !== slotId);
    const err = validateDaySlots(updatedSlots);

    setDayErrors((prev) => {
      const next = { ...prev };
      if (err) {
        next[dayKey] = err;
      } else {
        delete next[dayKey];
      }
      return next;
    });

    setSchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: updatedSlots,
      },
    }));
  };

  // Handle slot time change
  const handleSlotChange = (
    dayKey: DayKey,
    slotId: string,
    field: 'start' | 'end',
    newValue: string,
  ) => {
    const day = schedule[dayKey];
    const targetSlot = day.slots.find((s) => s.id === slotId);
    if (!targetSlot) return;

    const updatedStart = field === 'start' ? newValue : targetSlot.start;
    let updatedEnd = field === 'end' ? newValue : targetSlot.end;

    const startMins = timeToMinutes(updatedStart);
    let endMins = timeToMinutes(updatedEnd);

    if (field === 'start') {
      if (endMins <= startMins || endMins - startMins < 60) {
        endMins = Math.min(1440, startMins + 60);
        updatedEnd = minutesToTime(endMins);
      } else if (endMins - startMins > 480) {
        endMins = Math.min(1440, startMins + 480);
        updatedEnd = minutesToTime(endMins);
      }
    }

    const updatedSlots = day.slots.map((s) =>
      s.id === slotId ? { ...s, start: updatedStart, end: updatedEnd } : s,
    );

    const err = validateDaySlots(updatedSlots);

    setDayErrors((prev) => {
      const next = { ...prev };
      if (err) {
        next[dayKey] = err;
      } else {
        delete next[dayKey];
      }
      return next;
    });

    setSchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: sortSlots(updatedSlots),
      },
    }));
  };

  // Save Working Hours Schedule
  const executeSaveSchedule = (force: boolean = false) => {
    if (!userId) return;

    updateSchedule(
      {
        uid: userId,
        timezone,
        weekly_schedule: schedule,
        days: SCHEDULE_SYNC_DAYS_AHEAD,
        force,
        ai_responses_enabled: aiResponsesEnabled,
      },
      {
        onSuccess: (res) => {
          if (res?.hasConflicts && res.conflicts && res.conflicts.length > 0) {
            setConflictModal({
              isOpen: true,
              conflicts: res.conflicts,
            });
            return;
          }
          toast.success('Availability schedule saved successfully.');
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to update schedule');
        },
      },
    );
  };

  const handleSaveWorkingHours = () => {
    if (!timezone.trim()) {
      setErrorMsg('Please select a valid timezone to proceed.');
      return;
    }

    const errors: Partial<Record<DayKey, string>> = {};
    let hasAnyError = false;

    for (const dayConf of DAYS_CONFIG) {
      const dayData = schedule[dayConf.key];
      if (dayData?.enabled && dayData.slots?.length > 0) {
        const err = validateDaySlots(dayData.slots);
        if (err) {
          errors[dayConf.key] = err;
          hasAnyError = true;
        }
      }
    }

    if (hasAnyError || Object.keys(dayErrors).length > 0) {
      setDayErrors((prev) => ({ ...prev, ...errors }));
      return;
    }
    setErrorMsg('');

    executeSaveSchedule(false);
  };

  // Save AI Responses Toggle
  const handleSaveAiResponses = () => {
    if (!userId) return;

    updateSchedule(
      {
        uid: userId,
        ai_responses_enabled: aiResponsesEnabled,
        ai_responses_only: true,
      },
      {
        onSuccess: () => {
          toast.success('AI Responses setting saved successfully.');
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to save AI Responses setting');
        },
      },
    );
  };

  // Open Unavailable Dates Modal (with optional date focus)
  const handleOpenUnavailableModal = (dateStr: string | null) => {
    setFocusedDateForModal(dateStr);
    setUnavailableModalOpen(true);
  };

  // Format unavailable date row for main card list
  const formatUnavailableDateRow = (entry: UnavailableDateEntry) => {
    try {
      const [y, m, d] = entry.date.split('-').map(Number);
      const dateObj = new Date(y, m - 1, d);
      const weekday = dateObj.toLocaleDateString('en-GB', { weekday: 'long' });
      const formattedDate = dateObj.toLocaleDateString('en-GB', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const slotsText = entry.allDay
        ? 'All day'
        : entry.slots.map((s) => `${s.start} - ${s.end}`).join('  ');
      return { weekday, formattedDate, slotsText };
    } catch {
      return { weekday: '', formattedDate: entry.date, slotsText: 'Unavailable' };
    }
  };

  // Save unavailable dates to backend
  const handleSaveUnavailableDates = async (newDates: UnavailableDateEntry[]) => {
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }
    await updateScheduleAsync({
      uid: userId,
      unavailable_dates: newDates,
      unavailable_dates_only: true,
    });
    setUnavailableDatesList(newDates);
  };

  // Save calendar connection to backend
  const handleSaveCalendarConnection = async (calendarInfo: ConnectedCalendarInfo) => {
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }
    await updateScheduleAsync({
      uid: userId,
      connected_calendar: calendarInfo,
      calendar_connection_only: true,
    });
    setConnectedCalendar(calendarInfo);
  };

  return (
    <div className="schedule-settings-stack">
      {/* CARD 1: WORKING HOURS */}
      <div className="settings-card working-hours-card">
        {/* Timezone row */}
        <div className="working-hours-timezone-header">
          <div className="timezone-title-group">
            <svg
              className="globe-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span className="timezone-heading">TIME ZONE</span>
          </div>

          <div className="timezone-selector-wrapper">
            <TimezoneSelector
              value={timezone}
              onChange={(newTz) => {
                setTimezone(newTz);
                if (errorMsg) setErrorMsg('');
              }}
              error={errorMsg}
            />
          </div>
        </div>

        {/* Days List */}
        <div className="days-schedule-list">
          {DAYS_CONFIG.map(({ key, label }) => {
            const dayData = schedule?.[key] || DEFAULT_WEEKLY_SCHEDULE[key];
            const isEnabled = !!dayData?.enabled;
            const slots = dayData?.slots || [];
            const canAddSlot = isEnabled && slots.length < MAX_SLOTS_PER_DAY;
            const currentDayError = dayErrors[key];

            return (
              <div key={key} className="day-item-container">
                <div
                  className={`day-row ${!isEnabled ? 'disabled-day' : ''} ${
                    currentDayError ? 'has-error' : ''
                  }`}
                >
                  {/* Day Info & Toggle */}
                  <div className="day-info-col">
                    <span className="day-name">{label}</span>
                    <button
                      type="button"
                      className={`day-toggle-switch ${isEnabled ? 'checked' : ''}`}
                      onClick={() => handleToggleDay(key)}
                      role="switch"
                      aria-checked={isEnabled}
                      aria-label={`Toggle availability for ${label}`}
                    >
                      <span className="toggle-thumb" />
                    </button>
                  </div>

                  {/* Slots or Unavailable */}
                  <div className="day-slots-col">
                    {!isEnabled ? (
                      <span className="unavailable-text">Unavailable</span>
                    ) : (
                      <>
                        <div className="slots-wrapper">
                          {slots.map((slot) => {
                            const startMin = timeToMinutes(slot.start);
                            const minEnd = minutesToTime(Math.min(1440, startMin + 60));
                            const maxEnd = minutesToTime(Math.min(1440, startMin + 480));
                            const isSlotInvalid = isSlotConflicting(slot, slots);

                            return (
                              <div
                                key={slot.id}
                                className={`single-slot-box ${isSlotInvalid ? 'has-error' : ''}`}
                              >
                                <TimeSlotPicker
                                  value={slot.start}
                                  type="start"
                                  onChange={(newStart) =>
                                    handleSlotChange(key, slot.id, 'start', newStart)
                                  }
                                />

                                <span className="slot-separator">—</span>

                                <TimeSlotPicker
                                  value={slot.end}
                                  type="end"
                                  minTime={minEnd}
                                  maxTime={maxEnd}
                                  onChange={(newEnd) =>
                                    handleSlotChange(key, slot.id, 'end', newEnd)
                                  }
                                />

                                {slots.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn-remove-slot"
                                    onClick={() => handleRemoveSlot(key, slot.id)}
                                    aria-label="Remove slot"
                                    title="Remove slot"
                                  >
                                    &times;
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Add Slot Button */}
                        <button
                          type="button"
                          className={`btn-add-slot ${!canAddSlot ? 'disabled' : ''}`}
                          onClick={() => canAddSlot && handleAddSlot(key)}
                          disabled={!canAddSlot}
                          aria-label={`Add time slot for ${label}`}
                          title={
                            slots.length >= MAX_SLOTS_PER_DAY
                              ? `Maximum ${MAX_SLOTS_PER_DAY} slots per day`
                              : 'Add new slot'
                          }
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Inline Error */}
                {currentDayError && (
                  <div className="day-inline-error" role="alert">
                    <svg
                      className="error-icon"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{currentDayError}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Working Hours Save Button */}
        <div className="card-action-footer">
          <button
            type="button"
            className="btn-save-white"
            onClick={handleSaveWorkingHours}
            disabled={isUpdatingSchedule || isScheduleLoading}
          >
            {isUpdatingSchedule ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* CARD 2: UNAVAILABLE DATES & TIMES */}
      <div className="settings-card unavailable-dates-card">
        <div className="unavailable-card-header">
          <h2 className="unavailable-card-title">
            {unavailableDatesList.length > 0 ? 'Unavailable dates' : 'Unavailable dates & times'}
          </h2>
          <p className="unavailable-card-desc">
            {unavailableDatesList.length > 0
              ? "Block specific dates when you're not available"
              : "Add dates you're unavailable, or connect your calendar to automatically block time when you're busy."}
          </p>
        </div>

        {/* Configured Unavailable Dates List */}
        {unavailableDatesList.length > 0 && (
          <div className="unavailable-configured-list">
            {unavailableDatesList.map((item) => {
              const { weekday, formattedDate, slotsText } = formatUnavailableDateRow(item);
              return (
                <div
                  key={item.date}
                  className="configured-date-row"
                  onClick={() => handleOpenUnavailableModal(item.date)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleOpenUnavailableModal(item.date)}
                >
                  <div className="configured-date-info">
                    <span className="row-weekday">{weekday}</span>
                    <span className="row-pipe-divider">{'|'}</span>
                    <span className="row-formatted-date">{formattedDate}</span>
                    <span className="row-pipe-divider">{'|'}</span>
                    <span className="row-slots-preview">{slotsText}</span>
                  </div>
                  <svg
                    className="row-chevron-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}

        <div className="unavailable-actions-row">
          <button
            type="button"
            className={`btn-connect-calendar ${connectedCalendar?.connected ? 'is-connected' : ''}`}
            onClick={() => setCalendarModalOpen(true)}
          >
            <svg
              className="calendar-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>
              {connectedCalendar?.connected
                ? `${connectedCalendar.provider === 'google' ? 'Google' : 'Apple'} Calendar Connected`
                : 'Connect your Calendar'}
            </span>
          </button>

          <button
            type="button"
            className="btn-add-date-outline"
            onClick={() => handleOpenUnavailableModal(null)}
          >
            <svg
              className="plus-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Add date</span>
          </button>
        </div>
      </div>

      {/* CARD 3: AI RESPONSES */}
      <div className="settings-card ai-responses-card">
        <div className="ai-responses-content-row">
          <div className="ai-title-toggle-group">
            <span className="ai-responses-title">AI Responses</span>
            <button
              type="button"
              className={`day-toggle-switch ${aiResponsesEnabled ? 'checked' : ''}`}
              onClick={() => setAiResponsesEnabled((prev) => !prev)}
              role="switch"
              aria-checked={aiResponsesEnabled}
              aria-label="Toggle AI Responses"
            >
              <span className="toggle-thumb" />
            </button>
          </div>

          <div className="ai-responses-description-col">
            <p className="ai-desc-text">
              AI will respond to basic client questions outside your schedule hours to ensure they
              have 24/7 support. You can choose to turn off this feature at any time.
            </p>
          </div>
        </div>

        <div className="card-action-footer">
          <button
            type="button"
            className="btn-save-white"
            onClick={handleSaveAiResponses}
            disabled={isUpdatingSchedule}
          >
            {isUpdatingSchedule ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal for Day Disable */}
      <Modal
        open={confirmModal.isOpen}
        footer={null}
        onCancel={() => setConfirmModal({ isOpen: false, dayKey: null })}
        centered
        width={420}
        closeIcon={null}
        rootClassName="settings-modal"
        className="settings-modal"
        styles={settingsModalStyles}
      >
        <div className="settings-modal-content">
          <div className="modal-icon-badge warning">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h3 className="modal-title">Disable Day Availability?</h3>
          <p className="modal-text">
            Turning off availability for{' '}
            <strong>
              {DAYS_CONFIG.find((d) => d.key === confirmModal.dayKey)?.label || 'this day'}
            </strong>{' '}
            will remove all configured working slots on this day. Are you sure you want to proceed?
          </p>
          <div className="modal-actions-row">
            <button
              type="button"
              className="btn-modal-cancel"
              onClick={() => setConfirmModal({ isOpen: false, dayKey: null })}
            >
              Cancel
            </button>
            <button type="button" className="btn-modal-confirm" onClick={handleConfirmDisable}>
              Yes, Disable
            </button>
          </div>
        </div>
      </Modal>

      {/* Conflict Modal */}
      <Modal
        open={conflictModal.isOpen}
        footer={null}
        onCancel={() => setConflictModal({ isOpen: false, conflicts: [] })}
        centered
        width={500}
        closeIcon={null}
        rootClassName="settings-modal"
        className="settings-modal"
        styles={settingsModalStyles}
      >
        <div className="settings-modal-content">
          <div className="modal-icon-badge danger">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 className="modal-title">Booking Conflicts Detected</h3>
          <p className="modal-text">
            You have active client bookings during hours that were modified or removed.
          </p>
          <div className="conflicts-scroll-list">
            {conflictModal.conflicts.map((c, i) => (
              <div key={i} className="conflict-row">
                <span className="conflict-date">{c.date}</span>
                <span className="conflict-time">{c.time}</span>
                <span className="conflict-client">{c.clientName || 'Client Check-in'}</span>
              </div>
            ))}
          </div>
          <div className="modal-actions-row">
            <button
              type="button"
              className="btn-modal-cancel"
              onClick={() => setConflictModal({ isOpen: false, conflicts: [] })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-modal-confirm danger"
              onClick={() => {
                setConflictModal({ isOpen: false, conflicts: [] });
                executeSaveSchedule(true);
              }}
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      </Modal>

      {/* Connect Calendar Modal */}
      <ConnectCalendarModal
        isOpen={calendarModalOpen}
        onClose={() => setCalendarModalOpen(false)}
        connectedCalendar={connectedCalendar}
        onSaveCalendar={handleSaveCalendarConnection}
        isSaving={isUpdatingSchedule}
      />

      {/* Select Unavailable Dates Modal */}
      <UnavailableDatesModal
        isOpen={unavailableModalOpen}
        onClose={() => {
          setUnavailableModalOpen(false);
          setFocusedDateForModal(null);
        }}
        initialDates={unavailableDatesList}
        focusedDate={focusedDateForModal}
        onSave={handleSaveUnavailableDates}
        isSaving={isUpdatingSchedule}
      />
    </div>
  );
};
